import { useQuery } from "@apollo/client";
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";

import Header from "components/Header";
import { routes } from "constants/routes";
import { showErrorMessage } from "helpers/toastMessage";
import useDeviceType from "hooks/useDeviceType";
import MainLayout from "layouts/Main";
import { AppDispatch } from "store";
import { handleLoading } from "store/global/loader";
import { selectFactoringProfile } from "store/user/selector";
import FRListByStatusDesktop from "../../modules/FRListByStatusDesktop";
import SearchInput from "../../modules/FRListByStatusDesktop/components/SearchInput";
import StatusSummary from "../../Shared/StatusSummary";
import { QUERY_FUNDING_REQUEST_SUMMARY } from "./query";
import FRTypeFilterDropdown from "../../modules/FRTypeFilterDropdown";
import { useUserFeature } from "../../../../../hooks/useUserFeature";
import { FR_CONTRACT_TYPE } from "../../../../../constants/haulpay-broker";
import { selectAccount } from "../../../../../store/auth/selector";
import { IOption } from "../../../../../models/Form";
import { useMediaQuery } from "usehooks-ts";
import eventBus from "../../../../../common/EventBus";

export interface Filters {
  debtor?: string | null;
  status?: string | null;
  orderBy?: string;
  loadOrInvoiceOrDebtorOrStatus?: string | undefined | null;
  debtorName?: string | null;
  contractType?: string | null;
}

interface ManagePayHistoryState {
  debtor?: string;
  debtorName?: string;
  forceUpdate?: boolean;
}

export default function BrokerManagePay() {
  const RenderFundingRequestsComponent = () => {
    const dispatch = useDispatch<AppDispatch>();
    const { isDesktop } = useDeviceType();
    const navigate = useNavigate();
    const factoringProfile = useSelector(selectFactoringProfile);
    const { isBroker, isNonFactorRefactorEnabled } = useUserFeature();
    const { userPermission } = useSelector(selectAccount);
    const location = useLocation();
    const state = location.state as ManagePayHistoryState | undefined;
    const isSearchHeaderSquished = useMediaQuery("(max-width: 1160px)");
    const isTablet = useMediaQuery("(max-width: 980px)");

    useEffect(() => {
      if (state?.forceUpdate) {
        navigate(location.pathname, { replace: true });
      }
    }, [state?.forceUpdate, navigate, location.pathname]);

    useEffect(() => {
      if (isSearchHeaderSquished) {
        eventBus.emit("hideSidebar", true);
      } else {
        eventBus.emit("hideSidebar", false);
      }
    }, [isSearchHeaderSquished]);

    const sortDataKeys = (obj: any): any => {
      if (!obj || typeof obj !== "object") return {};
      const { all, ...rest } = obj;
      const sortedKeys = Object.keys(rest).sort((a, b) => a.localeCompare(b));

      const sortedObject: any = all ? { all } : {};
      sortedKeys.forEach((key) => {
        if (key !== "hold" || factoringProfile?.enable_hold_review_funding_request_status) {
          sortedObject[key] = rest[key];
        }
      });

      return sortedObject;
    };

    const {
      data: FRSummaryData,
      error,
      loading,
      refetch
    } = useQuery(QUERY_FUNDING_REQUEST_SUMMARY, {
      variables: {
        debtor: state?.debtor
      },
      fetchPolicy: "cache-and-network"
    });

    useEffect(() => {
      dispatch(handleLoading(loading));
      if (error) showErrorMessage(error.message);
    }, [FRSummaryData, error, loading]);

    const summaryData = useMemo(() => {
      return sortDataKeys(FRSummaryData?.fundingRequestsSummary?.data || {});
    }, [
      FRSummaryData?.fundingRequestsSummary?.data,
      factoringProfile?.enable_hold_review_funding_request_status
    ]);

    const handleSearch = (value: string) => {
      navigate(`${location.pathname}/all`, { state: { ...state, searchValue: value } });
    };

    const [selectedFrTypeOption, setSelectedFrTypeOption] = useState<IOption>();

    const handleFrTypeFilterChange = (selectedFrType: IOption) => {
      setSelectedFrTypeOption(selectedFrType);
      refetch({
        debtor: state?.debtor,
        contractType: selectedFrType.value
      });
    };

    const handleFrTypeFilterReset = () => {
      setSelectedFrTypeOption(undefined);
      refetch({
        debtor: state?.debtor,
        contractType: null
      });
    };

    const frTypeOptions = FR_CONTRACT_TYPE.filter(
      (request) => userPermission.available_contract_types[request.value]
    );

    return !isDesktop || isTablet ? (
      <>
        <SearchInput
          initialValue={""}
          onSearch={handleSearch}
          isMobile={true}
          iconClassName="absolute inset-y-0 my-auto text-placeholder left-4"
        />
        {isBroker && isNonFactorRefactorEnabled && frTypeOptions.length > 0 && (
          <FRTypeFilterDropdown
            style={{ flex: "0" }}
            frTypeOptions={frTypeOptions}
            handleOnChange={handleFrTypeFilterChange}
            handleResetClick={handleFrTypeFilterReset}
          />
        )}
        <StatusSummary
          summaryData={summaryData}
          debtor={state?.debtor}
          debtorName={state?.debtorName}
          frTypeOption={selectedFrTypeOption}
        />
      </>
    ) : (
      <FRListByStatusDesktop
        summaryData={summaryData}
        debtor={state?.debtor}
        refetchSummaryData={refetch}
        debtorName={state?.debtorName}
      />
    );
  };

  return (
    <MainLayout
      desktopTitle="Manage Pay"
      customHeader={<Header label="Manage Pay" goBackPath={routes.HAULPAY} isHideDesktop />}
      className="gap-5 px-5">
      <RenderFundingRequestsComponent />
    </MainLayout>
  );
}
